import { Link, useParams } from "react-router-dom";
import Seo from "components/common/Seo";
import ErrorPage from "pages/error/ErrorPage";
import CurriculumCta from "./CurriculumCta";
import CurriculumText from "./CurriculumText";
import {
    CURRICULUM_ACTIONS,
    CURRICULUM_LOGO,
    findCurriculumSport,
} from "./curriculumData";
import "./Curriculum.scss";

/** 앵커 클릭 시 라우터 히스토리를 건드리지 않고 해당 섹션으로만 스크롤한다. */
const scrollToSection = (event, id) => {
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
};

function DepthMarker({ depth }) {
    if (!depth) return null;
    return (
        <div className={`curriculum-depth curriculum-depth--${depth.tone}`}>
            {depth.value}
            <small>{depth.label}</small>
        </div>
    );
}

function StepRows({ rows }) {
    if (!rows?.length) return null;
    return (
        <dl className="curriculum-rows">
            {rows.map((row) => (
                <div className="curriculum-row" key={row.k}>
                    <dt className="curriculum-row__key">{row.k}</dt>
                    <dd className="curriculum-row__value">
                        <CurriculumText>{row.v}</CurriculumText>
                    </dd>
                </div>
            ))}
        </dl>
    );
}

function StepCard({ block }) {
    const classes = [
        "curriculum-card",
        `curriculum-card--${block.zone}`,
        block.variant ? `curriculum-card--${block.variant}` : "",
        block.violet ? "curriculum-card--violet" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <article className={classes} id={block.id}>
            <div
                className={`curriculum-node${
                    block.violetNode ? " curriculum-node--violet" : ""
                }`}
                aria-hidden="true"
            >
                {block.node}
            </div>
            <DepthMarker depth={block.depth} />
            {block.badge ? (
                <p className="curriculum-card__badge">{block.badge}</p>
            ) : null}
            <p className="curriculum-card__tag">{block.tag}</p>
            <h2 className="curriculum-card__title">
                {block.title}
                {block.sub ? (
                    <span className="curriculum-card__sub">{block.sub}</span>
                ) : null}
            </h2>

            <StepRows rows={block.rows} />

            {block.paragraphs?.map((paragraph) => (
                <p className="curriculum-card__paragraph" key={paragraph}>
                    <CurriculumText>{paragraph}</CurriculumText>
                </p>
            ))}
            {block.footnote ? (
                <p className="curriculum-card__footnote">
                    <CurriculumText>{block.footnote}</CurriculumText>
                </p>
            ) : null}

            {block.benefit ? (
                <p
                    className={`curriculum-benefit${
                        block.benefit.violet ? " curriculum-benefit--violet" : ""
                    }`}
                >
                    {block.benefit.text}
                </p>
            ) : null}
            {block.rec ? (
                <p className="curriculum-rec">
                    <CurriculumText>{block.rec}</CurriculumText>
                </p>
            ) : null}

            <CurriculumCta action={block.button} />
        </article>
    );
}

function SpecialCard({ special }) {
    return (
        <div className={`curriculum-special curriculum-special--${special.tone}`}>
            {special.image ? (
                <img
                    className="curriculum-special__image"
                    src={special.image.src}
                    alt={special.image.alt}
                    loading="lazy"
                />
            ) : null}
            <p
                className={`curriculum-chip curriculum-chip--${
                    special.tone === "onemore" ? "violet" : "cyan"
                }`}
            >
                {special.chip}
            </p>
            <h3 className="curriculum-special__title">
                {special.title}
                {special.titleEn ? (
                    <span className="curriculum-special__title-en">
                        {special.titleEn}
                    </span>
                ) : null}
            </h3>
            {special.copy ? (
                <p className="curriculum-special__copy">{special.copy}</p>
            ) : null}
            {special.desc ? (
                <p className="curriculum-special__desc">
                    <CurriculumText>{special.desc}</CurriculumText>
                </p>
            ) : null}
            {special.point ? (
                <p className="curriculum-special__point">
                    <span aria-hidden="true">✦</span>
                    {special.point}
                </p>
            ) : null}

            {special.items?.length ? (
                <ul className="curriculum-items">
                    {special.items.map((item) => (
                        <li className="curriculum-item" key={item.t}>
                            {item.num ? (
                                <span className="curriculum-item__num" aria-hidden="true">
                                    {item.num}
                                </span>
                            ) : null}
                            <span className="curriculum-item__title">{item.t}</span>
                            <span className="curriculum-item__desc">
                                <CurriculumText>{item.d}</CurriculumText>
                            </span>
                        </li>
                    ))}
                </ul>
            ) : null}

            {special.info ? (
                <p className="curriculum-special__info">
                    <CurriculumText>{special.info}</CurriculumText>
                </p>
            ) : null}

            <CurriculumCta action={special.button} />
        </div>
    );
}

function SideSpecial({ block }) {
    return (
        <div className="curriculum-side">
            <svg
                className="curriculum-side__connector"
                viewBox="0 0 58 56"
                aria-hidden="true"
                focusable="false"
            >
                <path
                    d="M 3 0 C 3 30, 50 14, 55 54"
                    fill="none"
                    stroke="#a06bff"
                    strokeWidth="2.5"
                    strokeDasharray="1 7"
                    strokeLinecap="round"
                />
            </svg>
            {block.bypass ? (
                <p className="curriculum-side__bypass">{block.bypass}</p>
            ) : null}
            <SpecialCard special={block} />
        </div>
    );
}

/**
 * /promotion/class/:sport — 종목별 커리큘럼 상세 화면.
 * 종목 데이터는 curriculumData.js 의 slug 로 찾는다.
 */
function CurriculumSport() {
    const { sub } = useParams();
    const sport = findCurriculumSport(sub);

    if (!sport) return <ErrorPage status={404} />;

    const {
        seoTitle,
        seoDescription,
        eyebrow,
        headline,
        lede,
        quickNav,
        surface,
        blocks,
        apartSections = [],
        faqSections = [],
        note,
    } = sport.page;

    return (
        <div className={`curriculum curriculum--sport curriculum--${sport.theme}`}>
            <Seo title={seoTitle} description={seoDescription} />

            <div className="curriculum__page">
                <div className="curriculum__backbar">
                    <Link to="/promotion/class">
                        <span aria-hidden="true">←</span>종목 다시 선택하기
                    </Link>
                </div>

                <header className="curriculum__header">
                    <img
                        className="curriculum__logo"
                        src={CURRICULUM_LOGO.navy}
                        alt={CURRICULUM_LOGO.alt}
                    />
                    <p className="curriculum__eyebrow">{eyebrow}</p>
                    <h1 className="curriculum__headline">
                        {headline.lead}
                        <br />
                        {headline.rest}
                        <span className="curriculum__headline-accent">
                            {headline.accent}
                        </span>
                    </h1>
                    <p className="curriculum__lede">
                        <CurriculumText>{lede.join("\n")}</CurriculumText>
                    </p>
                </header>

                <nav className="curriculum-qnav" aria-label="커리큘럼 단계 바로가기">
                    {quickNav.map((item) => (
                        <a
                            key={item.id}
                            href={`#${item.id}`}
                            className={item.hot ? "curriculum-qnav__link--hot" : undefined}
                            onClick={(event) => scrollToSection(event, item.id)}
                        >
                            <em>{item.em}</em>
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="curriculum-track">
                    <div className="curriculum-track__rail" aria-hidden="true" />
                    <p className="curriculum-track__surface" aria-hidden="true">
                        {surface}
                    </p>

                    {blocks.map((block) =>
                        block.kind === "special" ? (
                            <SideSpecial block={block} key={block.title} />
                        ) : (
                            <StepCard block={block} key={block.id} />
                        )
                    )}
                </div>

                {apartSections.map((section) => (
                    <section className="curriculum-apart" id={section.id} key={section.head}>
                        <div className="curriculum-apart__head">
                            <div className="curriculum-apart__line" aria-hidden="true" />
                            <h2 className="curriculum-apart__title">
                                <CurriculumText>{section.head}</CurriculumText>
                            </h2>
                        </div>
                        <SpecialCard special={section.special} />
                    </section>
                ))}

                {faqSections.map((section) => (
                    <section className="curriculum-faq" key={section.title}>
                        <h2 className="curriculum-faq__title">{section.title}</h2>
                        {section.items.map((item) => (
                            <details key={item.q} open={item.open}>
                                <summary>{item.q}</summary>
                                <p>
                                    <CurriculumText>{item.a}</CurriculumText>
                                </p>
                            </details>
                        ))}
                    </section>
                ))}

                <footer className="curriculum__footer">
                    <div className="curriculum__actions">
                        <CurriculumCta
                            action={{ ...CURRICULUM_ACTIONS.schedule, tone: "schedule" }}
                        />
                        <CurriculumCta
                            action={{ ...CURRICULUM_ACTIONS.kakaoCourse, tone: "kakao" }}
                        />
                    </div>
                    <p className="curriculum__note">
                        <CurriculumText>{note.join("\n")}</CurriculumText>
                    </p>
                    <img
                        className="curriculum__logo curriculum__logo--footer"
                        src={CURRICULUM_LOGO.white}
                        alt={CURRICULUM_LOGO.alt}
                    />
                </footer>
            </div>
        </div>
    );
}

export default CurriculumSport;
